/* v78 (2026-05-08): Filter same-line products from upsell recommendations.
   v78b (2026-05-08): backfill — if dedupe shrinks the upsell below 3 cards,
   fetch additional recommendations from /recommendations/products and inject
   the first N unique-line ones to top up to 3.
   v.5.9A (2026-05-17): also filter ADDON products from upsell. Shopify's
   /recommendations/products surfaces high-cooccurrence items, which for
   Caroline's catalog routinely returns swieczka-cyferka, swieczki-dlugie,
   napis-na-torcie, etc. — but those are ALREADY available in the
   Personalizacja card on the same PDP, so recommending them again
   duplicates the offer and looks low-effort. Block by handle (canonical +
   all 4 locale variants per the LOCALIZED_HANDLES map in custom.js).

   Caroline + Pat 2026-05-08: Shopify's /recommendations/products algorithm
   routinely surfaces products from the same line on the same product's PDP.
   Caroline doesn't want duplicates — wants cross-line discovery.

   Strategy:
   1. Title-first-word match. Caroline's naming convention groups product
      variants under a shared first word ("Duet", "Mus czekoladowy",
      "Miłości") so dropping any recommendation whose first word matches
      the current product's first word reliably catches the same-line case.
   2. After dedupe, if visible cards < 3, fetch /recommendations/products
      with a higher limit, filter same-line + already-shown + current
      product, and append unique-line cards until visible >= 3 (or backfill
      exhausted).
   3. (v.5.9A) Addon dedupe via handle blocklist — runs before backfill so
      backfill replaces filtered addons with real cross-sell candidates.

   Affects every product template uniformly via shared upsell holder.
   Loaded via snippets/zip-cookies.liquid.

   BS-37 (2026-05-31): now the SOLE client-side upsell filler. The inline
   recommendations fetch in sections/product.liquid and topUpUpsell() in
   custom.js were removed — this module does primary fill + dedupe + backfill
   in one place, filtering before insert.

   BS-55 (2026-06-04): partial-list top-up. Previously ANY product with >=1
   manual pick (theme.upsell_list) was stamped data-upsell-curated="1" by
   sections/product.liquid and this module bailed entirely — so a 1- or
   2-item list rendered only 1-2 cards and never reached 3 (e.g. Zestaw mini
   kamyków showed a single upsell). The bail decision now keys off the ACTUAL
   count of server-rendered manual cards instead of the curated flag:
     >=3 manual cards -> slot already full, leave as-is (full curated).
     1-2 manual cards -> PROTECT them from dedupe, then backfill to 3.
     0  manual cards  -> backfill to 3 (unchanged algo path).
   Manual cards are stamped data-upsell-manual="1" so the same-line / addon
   filters can never strip an editorial pick; backfill (whose seen-set
   already includes every currently-shown card id) only adds cross-line algo
   picks into the remaining slots. This honours the original v.5.9B intent
   (don't let the algo REPLACE editorial picks) while still guaranteeing 3.
   data-upsell-curated is no longer read here (kept on the markup for
   reference); isCuratedMode() is retained but unused.

   BS-81 (2026-06-05): fill-to-3 reliability. The /recommendations section
   (sections/api-product-upsell-item.liquid) renders each product as an OUTER
   .quickview wrapper that CONTAINS an inner .product-clip. backfill() used
   the selector '.quickview, .product-clip', which matched BOTH the parent
   wrapper AND its child clip for every product. Appending the parent and then
   the child via appendChild relocated the clip OUT of the wrapper, leaving an
   empty .quickview shell occupying a slot — so e.g. Zestaw mini kamyków
   rendered only 2 VISIBLE cards (1 manual + 1 real + 1 invisible empty shell)
   instead of 3. The seen-set also missed the duplicate because the wrapper was
   keyed by its numeric quickview id while the clip was keyed by h:handle.
   Two surgical fixes, both confined to backfill():
   (1) fetchCards() now keeps only TOP-LEVEL cards (drops any card nested
       inside another matched card) -> exactly one node per product, never an
       empty shell.
   (2) De-dupe is now HANDLE-based as well as id-based; the seen-set is seeded
       with the current product handle + every currently-shown card's handle,
       so a product can't be added twice through the id-vs-handle key mismatch.
   Source field is unchanged (still theme.upsell_list); the algo fill path is
   retained. */

(function () {
  if (window.__batidaUpsellDedupeLoaded) return;
  window.__batidaUpsellDedupeLoaded = true;

  // v.5.9B (2026-05-17): if the PDP has editorial curated upsells set
  // via product.metafields.custom.upsell_handles, sections/product.liquid
  // already rendered those server-side and stamps data-upsell-curated="1"
  // on the [data-upsell-hybrid] block. In that case skip dedupe + backfill
  // entirely — curated is authoritative and we don't want to filter out
  // an editorial pick because it shares a first word with the current
  // product or sits in the addon blocklist. Match the same gate in the
  // inline hydration script in sections/product.liquid.
  // BS-55 (2026-06-04): retained for reference but no longer used by init();
  // the bail decision now counts actual server-rendered manual cards so a
  // 1-2 item list can be topped up to 3 (see header).
  function isCuratedMode() {
    var block = document.querySelector('[data-upsell-hybrid]');
    return !!(block && block.getAttribute('data-upsell-curated') === '1');
  }

  var TARGET_VISIBLE = 3;
  var BACKFILL_LIMIT = 12;

  // v.5.9A (2026-05-17): addon SKUs to NEVER show in the upsell slot —
  // they already live in the Personalizacja card on every PDP, so
  // recommending them here would duplicate the offer. List covers
  // canonical PL handles + every locale variant T Lab/Translate & Adapt
  // generated (mirrors the LOCALIZED_HANDLES map in custom.js ~L2384).
  // To extend: add the new addon's handle here in every locale where
  // it's translated. Caroline can also use the v.5.9B curated metafield
  // to OVERRIDE the upsell slot on specific PDPs — this blocklist is
  // the default-deny floor for the algo path.
  var ADDON_HANDLES = {
    // canonical PL
    'napis-na-torcie': 1,
    'napisz-swoj-list': 1,
    'swieczka-cyferka': 1,
    'swieczki-dlugie': 1,
    'zestaw-jednorazowy': 1,
    'bilecik': 1,
    // German (T Lab handle translations)
    'blatt': 1,
    'kerzencit': 1,
    'lange-kerzen': 1,
    'holzplatte-und-besteck': 1,
    // English
    'sheet': 1,
    'long-candles': 1,
    'number-candle': 1,
    'wooden-plate-and-cutlery': 1,
    // Ukrainian
    'примітка-1': 1,
    'довгі-свічки': 1,
    'candle-cit-1': 1,
    'тарілка-та-екологічні-столові-прилади': 1,
    // French
    'note-1': 1,
    'bougies-longues': 1,
    'bougie-citation': 1,
    'plaque-et-couverts-ecologiques': 1
  };

  function normalize(str) {
    if (!str) return '';
    try {
      return String(str)
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
    } catch (e) {
      return String(str).toLowerCase().replace(/[^a-z0-9]/g, '');
    }
  }

  function getCurrentProductFirstWord() {
    var selectors = [
      'h1.product__page__title',
      '.product__block__title h1',
      '.product__page__title',
      'h1.product-title',
      '.product-page h1',
      'main h1',
      'h1'
    ];
    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (el && el.textContent && el.textContent.trim()) {
        var text = el.textContent.trim().split(/\s+/)[0];
        return normalize(text);
      }
    }
    return '';
  }

  function getUpsellBlockMeta() {
    var block = document.querySelector('[data-upsell-hybrid]');
    if (!block) return null;
    return {
      block: block,
      productId: block.dataset.productId || '',
      // BS-81: surface the current product handle so backfill can seed the
      // seen-set with it (handle-level de-dupe, see backfill()).
      handle: block.dataset.productHandle || '',
      manualUpsellId: block.dataset.manualUpsellId || '',
      limit: parseInt(block.dataset.limit || '3', 10) || 3
    };
  }

  function getCardProductId(card) {
    // Pipeline marks each card with `quickview <productId>-btn-div`
    var m = (card.className || '').match(/quickview\s+(\d+)-btn-div/);
    if (m && m[1]) return m[1];
    // Alternative: data-product-id attribute
    if (card.dataset && card.dataset.productId) return card.dataset.productId;
    // Fallback: use the product HANDLE from the link URL (works for both
    // .quickview and .product-clip and any future Pipeline card variant).
    // Prefix `h:` so handle keys never collide with numeric ID keys.
    var link = card.querySelector('a[href*="/products/"]');
    if (link) {
      var path = link.pathname || '';
      var m2 = path.match(/\/products\/([^\/?#]+)/);
      if (m2 && m2[1]) return 'h:' + m2[1];
    }
    return '';
  }

  // v.5.9A: extract the raw URL handle from a card's product link.
  // Returns the localized handle as it appears in the URL (so the
  // ADDON_HANDLES map needs entries for every locale variant — see top).
  function getCardHandle(card) {
    if (!card) return '';
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return '';
    var path = link.pathname || '';
    var m = path.match(/\/products\/([^\/?#]+)/);
    if (!m) return '';
    try { return decodeURIComponent(m[1]).toLowerCase(); }
    catch (e) { return m[1].toLowerCase(); }
  }

  function isAddonCard(card) {
    var handle = getCardHandle(card);
    return !!(handle && ADDON_HANDLES[handle]);
  }

  function getCardTitle(card) {
    var titleEl = card.querySelector(
      '.product-clip__title__text, .product-clip__title, [class*="product-clip__title"], a[href*="/products/"] [class*="title"]'
    );
    if (!titleEl) {
      var link = card.querySelector('a[href*="/products/"]');
      if (link) titleEl = link;
    }
    if (!titleEl) return '';
    return (titleEl.textContent || '').trim();
  }

  function getItemsContainer() {
    return document.querySelector('.upsell__holder--hybrid .upsell__holder__items') ||
           document.querySelector('[data-upsell-items]');
  }

  function getCurrentCards() {
    var holder = document.querySelector('.upsell__holder--hybrid');
    if (!holder) return [];
    return Array.from(holder.querySelectorAll(
      '.upsell__holder__items > .quickview, .upsell__holder__items > .product-clip'
    ));
  }

  // BS-55 (2026-06-04): manual (server-rendered editorial) picks are stamped
  // with this attribute by init() before any filtering runs, so dedupe() and
  // dedupeAddons() can skip them — an editorial pick must never be removed
  // for sharing a first word with the current product or for being an addon.
  function isProtectedCard(card) {
    return !!(card && card.getAttribute('data-upsell-manual') === '1');
  }

  function dedupe() {
    try {
      var firstWord = getCurrentProductFirstWord();
      if (!firstWord) return;

      var cards = getCurrentCards();
      cards.forEach(function (card) {
        if (isProtectedCard(card)) return; // BS-55: never strip a manual pick
        if (card.getAttribute('data-batida-upsell-deduped') === '1') return;
        var title = getCardTitle(card);
        if (!title) return;
        var cardFirst = normalize(title.split(/\s+/)[0]);
        if (!cardFirst) return;
        if (cardFirst === firstWord) {
          card.setAttribute('data-batida-upsell-deduped', '1');
          card.remove();
        }
      });
    } catch (e) {
      try { console.warn('batida upsell dedupe error:', e); } catch (_) {}
    }
  }

  // v.5.9A (2026-05-17): drop any card whose handle is in ADDON_HANDLES.
  // Idempotent — safe to call alongside dedupe() repeatedly.
  function dedupeAddons() {
    try {
      var cards = getCurrentCards();
      cards.forEach(function (card) {
        if (isProtectedCard(card)) return; // BS-55: never strip a manual pick
        if (card.getAttribute('data-batida-upsell-addon-filtered') === '1') return;
        if (isAddonCard(card)) {
          card.setAttribute('data-batida-upsell-addon-filtered', '1');
          card.remove();
        }
      });
    } catch (e) {
      try { console.warn('batida upsell addon-dedupe error:', e); } catch (_) {}
    }
  }

  function countVisibleCards() {
    return getCurrentCards().filter(function (c) { return c.offsetParent !== null; }).length;
  }

  function backfill() {
    try {
      var visible = countVisibleCards();
      if (visible >= TARGET_VISIBLE) return; // already filled

      var meta = getUpsellBlockMeta();
      if (!meta || !meta.productId) return;

      var firstWord = getCurrentProductFirstWord();
      if (!firstWord) return;

      var itemsContainer = getItemsContainer();
      if (!itemsContainer) return;

      // Build "already-shown" set of product IDs to avoid duplicates.
      // BS-81: key by BOTH numeric id AND h:handle. The recommendations
      // section renders each product as an outer .quickview (keyed by numeric
      // id) wrapping an inner .product-clip (keyed by h:handle); keying only
      // one of those let the same product be added twice (the empty-shell
      // duplicate). Seed with the current product handle + every shown card's
      // handle so neither representation can slip past the seen check.
      var seen = {};
      seen[String(meta.productId)] = true;
      if (meta.manualUpsellId) seen[String(meta.manualUpsellId)] = true;
      if (meta.handle) seen['h:' + meta.handle] = true;
      getCurrentCards().forEach(function (c) {
        var pid = getCardProductId(c);
        if (pid) seen[pid] = true;
        var h = getCardHandle(c);
        if (h) seen['h:' + h] = true;
      });

      // Fetch additional recommendations from BOTH intents in parallel and merge.
      // v78e (2026-05-08): added complementary intent — Shopify's frequently-
      // bought-together algorithm. Returns 0 results for products without
      // curated complementary relationships, so we ALSO fetch the default
      // `related` intent and merge — guarantees we always have the broadest
      // possible candidate pool. Complementary cards rank first (cross-sell
      // wins over similarity), then related fills the rest.
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var base = root.replace(/\/$/, '') +
        '/recommendations/products?section_id=api-product-upsell-item&product_id=' +
        encodeURIComponent(meta.productId) + '&limit=' + BACKFILL_LIMIT;
      var urls = [base + '&intent=complementary', base + '&intent=related'];

      function fetchCards(url) {
        return fetch(url, { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (html) {
            if (!html) return [];
            var doc = new DOMParser().parseFromString(html, 'text/html');
            // BS-81: each product is an OUTER .quickview wrapper that CONTAINS
            // an inner .product-clip. Selecting both matched parent+child per
            // product; appending the parent then the child relocated the clip
            // out of the wrapper, leaving an empty quickview shell in a slot.
            // Keep only TOP-LEVEL cards (drop any card nested inside another
            // matched card) so we append exactly one node per product.
            var nodes = Array.from(doc.querySelectorAll('.quickview, .product-clip'));
            return nodes.filter(function (el) {
              return !(el.parentElement && el.parentElement.closest('.quickview, .product-clip'));
            });
          })
          .catch(function () { return []; });
      }

      Promise.all(urls.map(fetchCards)).then(function (lists) {
        // Concatenate complementary first, then related — preserves the
        // intent ordering preference (cross-sell > semantic similarity).
        var newCards = lists[0].concat(lists[1]);
        if (!newCards.length) return;

        var added = 0;
        var stillNeeded = TARGET_VISIBLE - countVisibleCards();
        if (stillNeeded <= 0) return;

        newCards.forEach(function (card) {
          if (added >= stillNeeded) return;
          // BS-37 (2026-05-31): require a real product link. Shopify's
          // recommendations section response can include non-product nodes
          // (e.g. a template/drawer fragment) that still match
          // .quickview/.product-clip; never append those. Showing fewer real
          // cards beats padding the slot with a junk node.
          if (!card.querySelector || !card.querySelector('a[href*="/products/"]')) return;
          var pid = getCardProductId(card);
          var handle = getCardHandle(card); // BS-81: handle-level de-dupe key
          if (pid && seen[pid]) return; // already shown or current product (or already added by complementary)
          if (handle && seen['h:' + handle]) return; // BS-81: same product via the other representation
          // v.5.9A: skip addon SKUs even during backfill so the algo can't
          // sneak a candle/plate/note back into the slot via a second fetch.
          if (isAddonCard(card)) return;
          var title = getCardTitle(card);
          if (!title) return;
          var cardFirst = normalize(title.split(/\s+/)[0]);
          if (cardFirst === firstWord) return; // same line
          // Mark + insert
          if (pid) seen[pid] = true;
          if (handle) seen['h:' + handle] = true; // BS-81
          card.setAttribute('data-batida-upsell-backfilled', '1');
          itemsContainer.appendChild(card);
          added++;
        });
      }).catch(function () {});
    } catch (e) {
      try { console.warn('batida upsell backfill error:', e); } catch (_) {}
    }
  }

  // v78d (2026-05-08): backfill runs at most once per page load.
  // Without this gate, every setTimeout retry + MutationObserver fire
  // triggered another fetch+append cycle, ballooning the DOM to 12+ cards
  // and creating apparent duplicates. Dedupe is idempotent so it can run
  // many times safely; backfill must not.
  var backfillRan = false;
  function maybeBackfillOnce() {
    if (backfillRan) return;
    backfillRan = true;
    backfill();
  }

  // v.5.9A: combined dedupe pass — same-line + addon. Both are
  // idempotent and cheap so they run together at every tick.
  function runAllDedupes() {
    dedupeAddons(); // run first: kills addons before same-line check
    dedupe();
  }

  function init() {
    // BS-55 (2026-06-04): partial-list top-up. The bail decision now keys off
    // the ACTUAL count of server-rendered manual cards rather than the
    // data-upsell-curated flag (which sections/product.liquid sets for ANY
    // manual pick and used to suppress fill entirely):
    //   >=3 manual cards -> slot already full, leave as-is (full curated).
    //   1-2 manual cards -> protect them, then backfill to 3.
    //   0  manual cards  -> backfill to 3 (unchanged algo path).
    var initialCards = getCurrentCards();
    if (initialCards.length >= TARGET_VISIBLE) return; // full curated — nothing to do

    // Protect manual picks so dedupe()/dedupeAddons() can't strip them, and
    // so backfill's seen-set (built from current cards) won't re-add them.
    initialCards.forEach(function (c) {
      c.setAttribute('data-upsell-manual', '1');
    });

    // BS-37 (2026-05-31): this module is the SOLE upsell filler. On a 0-manual
    // PDP the box starts empty, so fire the fill immediately. backfill()
    // filters addon + same-line cards BEFORE inserting, so cards never
    // appear-then-disappear. Manual picks (1-2) stay put and backfill tops up.
    runAllDedupes();        // only touches non-protected (backfilled) cards
    maybeBackfillOnce();    // PRIMARY fill / top-up
    setTimeout(runAllDedupes, 200);
    setTimeout(runAllDedupes, 600);

    // Late retries — only dedupe, never re-trigger backfill.
    setTimeout(runAllDedupes, 2000);
    setTimeout(runAllDedupes, 4000);

    // MutationObserver: if cards get re-populated externally, re-run dedupe
    // only — never backfill.
    try {
      var holder = document.querySelector('.upsell__holder--hybrid');
      if (holder) {
        var __mo = new MutationObserver(function () {
          clearTimeout(window.__batidaUpsellDedupeMoTimer);
          window.__batidaUpsellDedupeMoTimer = setTimeout(runAllDedupes, 100);
          clearTimeout(window.__batidaUpsellDedupeIdle);
          window.__batidaUpsellDedupeIdle = setTimeout(function () { try { __mo.disconnect(); } catch (e) {} }, 5000);
        });
        __mo.observe(holder, { childList: true, subtree: true });
        window.__batidaUpsellDedupeIdle = setTimeout(function () { try { __mo.disconnect(); } catch (e) {} }, 5000);
      }
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();